import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { STATS } from "@/data/content";

const VALUES = [
  { title: "Revenue over vanity", body: "We optimize for leads and sales, not likes. Every dollar you spend should come back multiplied." },
  { title: "Radical transparency", body: "Clear reporting, no jargon, no smoke and mirrors. You'll always know exactly what's working." },
  { title: "Small-business obsessed", body: "We only work with small businesses. Your growth is personal to us — you're not a number." },
  { title: "Speed & craft", body: "We move fast without cutting corners. Quality execution, shipped quickly." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden pt-40 pb-16">
        <div className="pointer-events-none absolute -left-32 top-24 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">About us</span></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-5xl font-display text-5xl font-black leading-[0.95] tracking-tighter text-slate-900 sm:text-6xl lg:text-7xl">
              We're the growth partner small businesses actually deserve.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 pb-8">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl">
              <img src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4" alt="Massive Growth team collaborating" className="h-[380px] w-full object-cover lg:h-[520px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Big-agency firepower, without the big-agency ego.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 text-base leading-relaxed text-slate-600">
              <p>Massive Growth Marketing was founded on a simple belief: small businesses shouldn't have to choose between an expensive agency that ignores them and a cheap freelancer who disappears.</p>
              <p>We bring together specialists across web design, SEO, social media, paid advertising, email & SMS, and reputation management — a full growth team, focused entirely on your results.</p>
              <p>No long contracts. No confusing reports. Just a partner obsessed with growing your revenue.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 navy-mesh py-16 text-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-10 px-6 lg:grid-cols-4 lg:px-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="text-center lg:text-left">
              <p className="font-display text-5xl font-black tracking-tighter text-brand">{s.value}</p>
              <p className="mt-2 text-sm text-slate-400">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative z-10 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Our values</span>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">What we stand for.</h2>
          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 md:grid-cols-2">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 2) * 0.08}>
                <div className="h-full bg-white p-10">
                  <span className="font-display text-2xl font-black text-brand">0{i + 1}</span>
                  <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-slate-900">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="rounded-3xl bg-brand px-8 py-14 text-center text-white lg:px-20">
            <h2 className="font-display text-3xl font-black tracking-tighter sm:text-5xl">Let's grow together.</h2>
            <Link to="/contact" data-testid="about-cta-button" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-bold text-brand transition-transform hover:scale-[1.02]">Start the conversation <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
